// client/src/components/contratos/ConfirmarRetiradaModal.js
// Confirmação de retirada de equipamento (contratos com Transportador como
// contraparte) — ação atômica única: escolhe motorista/veículo, marca
// quantidade retirada por item do contrato, e assina como motorista, tudo
// numa submissão (PUT /contratos/:id/confirmar-retirada). Motorista/veículo
// vêm de contrato.cliente.motoristas[]/veiculos[] (Transportador populado
// inteiro em getContrato), pré-selecionados a partir de
// contrato.transportadorDesignado se já definido na criação.
import React, { useRef, useState } from 'react';
import { Save, Loader2 } from 'lucide-react';
import FormModal from '../layout/FormModal';
import AssinaturaCanvas from '../common/AssinaturaCanvas';
import { useConfirmarRetirada } from '../../hooks/useContratos';
import useAlert from '../../hooks/useAlert';

const ConfirmarRetiradaModal = ({ contrato, onClose }) => {
    const { setAlert } = useAlert();
    const confirmarRetirada = useConfirmarRetirada();
    const canvasRef = useRef(null);

    const transportador = typeof contrato.cliente === 'object' ? contrato.cliente : null;
    const motoristas = transportador?.motoristas || [];
    const veiculos = transportador?.veiculos || [];

    const [motoristaId, setMotoristaId] = useState(contrato.transportadorDesignado?.motoristaId || '');
    const [veiculoId, setVeiculoId] = useState(contrato.transportadorDesignado?.veiculoId || '');
    const [quantidades, setQuantidades] = useState(() =>
        Object.fromEntries((contrato.itens || []).map((item) => [item._id, item.quantidade]))
    );
    const [observacoes, setObservacoes] = useState('');

    const handleQuantidadeChange = (itemId, value) => {
        setQuantidades((prev) => ({ ...prev, [itemId]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!motoristaId) {
            setAlert('Selecione o motorista que está retirando o equipamento.', 'warning');
            return;
        }

        const assinatura = canvasRef.current?.getSignature();
        if (!assinatura) {
            setAlert('Colete a assinatura do motorista antes de confirmar.', 'warning');
            return;
        }

        const itensRetirados = (contrato.itens || [])
            .map((item) => ({ itemContrato: item._id, quantidadeRetirada: Number(quantidades[item._id]) || 0 }))
            .filter((ir) => ir.quantidadeRetirada > 0);

        if (itensRetirados.length === 0) {
            setAlert('Informe ao menos um item com quantidade retirada maior que zero.', 'warning');
            return;
        }

        confirmarRetirada.mutate(
            { id: contrato._id, motoristaId, veiculoId: veiculoId || undefined, itensRetirados, assinatura, observacoes },
            { onSuccess: onClose }
        );
    };

    return (
        <FormModal title={`Confirmar Retirada — Contrato Nº ${contrato.numeroContrato}`} onClose={onClose} size="large">
            <form onSubmit={handleSubmit}>
                <div className="form-grid-container-cols-2">
                    <div className="form-group">
                        <label className="form-label">Motorista*</label>
                        <select className="form-input" value={motoristaId} onChange={(e) => setMotoristaId(e.target.value)} required>
                            <option value="">Selecione</option>
                            {motoristas.map((m) => (
                                <option key={m._id} value={m._id}>{m.nomeCompleto} — CPF {m.cpf}</option>
                            ))}
                        </select>
                    </div>
                    <div className="form-group">
                        <label className="form-label">Veículo</label>
                        <select className="form-input" value={veiculoId} onChange={(e) => setVeiculoId(e.target.value)}>
                            <option value="">Nenhum</option>
                            {veiculos.map((v) => (
                                <option key={v._id} value={v._id}>{v.modelo} — placa {v.placa}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="form-group full-width">
                    <label className="form-label">Itens a retirar</label>
                    <div className="dynamic-list-container">
                        <div className="dynamic-list-item-header">
                            <div className="col-desc">Item</div>
                            <div className="col-qtd">Contratado</div>
                            <div className="col-qtd">Retirando</div>
                        </div>
                        {(contrato.itens || []).map((item) => (
                            <div key={item._id} className="dynamic-list-item">
                                <div className="col-desc">{item.descricaoItemNoContrato}</div>
                                <div className="col-qtd">{item.quantidade}</div>
                                <div className="col-qtd">
                                    <input
                                        type="number"
                                        min="0"
                                        max={item.quantidade}
                                        className="form-input"
                                        value={quantidades[item._id] ?? item.quantidade}
                                        onChange={(e) => handleQuantidadeChange(item._id, e.target.value)}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="form-group full-width">
                    <label className="form-label">Assinatura do Motorista*</label>
                    <AssinaturaCanvas ref={canvasRef} />
                </div>

                <div className="form-group full-width">
                    <label className="form-label">Observações (opcional)</label>
                    <textarea className="form-input" rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
                </div>

                <div className="form-actions">
                    <button type="button" onClick={onClose} className="action-button action-button-secondary">Cancelar</button>
                    <button type="submit" disabled={confirmarRetirada.isPending} className="action-button action-button-primary">
                        {confirmarRetirada.isPending ? <Loader2 size={18} className="animate-spin icon" /> : <Save size={18} className="icon" />}
                        Confirmar Retirada
                    </button>
                </div>
            </form>
        </FormModal>
    );
};

export default ConfirmarRetiradaModal;
